import { useMemo } from "react";

/**
 * Generic interface for hierarchical items
 */
export interface HierarchicalItem {
  id: string;
  children?: HierarchicalItem[];
}

/**
 * Hook that manages hierarchical relationships between items
 * @param items Hierarchical items to analyze
 * @returns Object containing relationship maps and helper functions
 */
export function useHierarchyRelations<T extends HierarchicalItem>(items: T[]) {
  // Build maps of relationships between items
  const { itemsMap, childrenMap, parentMap } = useMemo(() => {
    const itemsMap = new Map<string, T>();
    const childrenMap = new Map<string, string[]>();
    const parentMap = new Map<string, string>();

    // Helper function to recursively build the maps
    const buildMaps = (items: T[], parentId?: string) => {
      for (const item of items) {
        itemsMap.set(item.id, item);

        if (parentId) {
          parentMap.set(item.id, parentId);
          const parentChildren = childrenMap.get(parentId) || [];
          childrenMap.set(parentId, [...parentChildren, item.id]);
        }

        if (item.children && item.children.length > 0) {
          buildMaps(item.children as T[], item.id);
        }
      }
    };

    buildMaps(items);

    return { itemsMap, childrenMap, parentMap };
  }, [items]);

  /**
   * Gets all descendants of a given item
   * @param itemId ID of the item
   * @returns Array of descendant IDs
   */
  const getAllDescendants = (itemId: string): string[] => {
    const children = childrenMap.get(itemId) || [];
    const descendants = [...children];

    for (const childId of children) {
      descendants.push(...getAllDescendants(childId));
    }

    return descendants;
  };

  /**
   * Gets all ancestors of a given item
   * @param itemId ID of the item
   * @returns Array of ancestor IDs
   */
  const getAncestors = (itemId: string): string[] => {
    const ancestors: string[] = [];
    let currentId = itemId;

    while (parentMap.has(currentId)) {
      const parentId = parentMap.get(currentId);
      if (!parentId) break;

      ancestors.push(parentId);
      currentId = parentId;
    }

    return ancestors;
  };

  /**
   * Checks if all children of a parent are in a selection array
   * @param parentId ID of the parent item
   * @param selected Array of selected item IDs
   * @returns true if all children are selected
   */
  const areAllChildrenSelected = (
    parentId: string,
    selected: string[]
  ): boolean => {
    const children = childrenMap.get(parentId) || [];
    return (
      children.length > 0 &&
      children.every((childId) => selected.includes(childId))
    );
  };

  /**
   * Checks if any children of a parent are in a selection array
   * @param parentId ID of the parent item
   * @param selected Array of selected item IDs
   * @returns true if any children are selected
   */
  const areAnyChildrenSelected = (
    parentId: string,
    selected: string[]
  ): boolean => {
    const children = childrenMap.get(parentId) || [];
    return children.some((childId) => selected.includes(childId));
  };

  /**
   * Filters a list of IDs to show only the highest items in each hierarchy branch
   * @param ids Array of item IDs to filter
   * @returns Array containing only the highest items in hierarchy
   */
  const filterToHighestAncestors = (ids: string[]): string[] => {
    if (ids.length === 0) return [];

    const idsSet = new Set(ids);

    // Use native array filter to keep only items that should be displayed
    return ids.filter((id) => {
      // Remove if any ancestor is selected
      // (We only want to show the highest level selected item)
      const ancestors = getAncestors(id);
      return !ancestors.some((ancestorId) => idsSet.has(ancestorId));
    });
  };

  /**
   * Gets the depth level of an item in the hierarchy
   * @param itemId ID of the item
   * @returns Depth level (0 for root items)
   */
  const getItemDepth = (itemId: string): number => {
    return getAncestors(itemId).length;
  };

  /**
   * Gets all leaf nodes (items without children)
   * @returns Array of leaf node IDs
   */
  const getLeafNodes = (): string[] => {
    return Array.from(itemsMap.keys()).filter(
      (id) => !childrenMap.has(id) || childrenMap.get(id)?.length === 0
    );
  };

  /**
   * Gets all root nodes (items without parents)
   * @returns Array of root node IDs
   */
  const getRootNodes = (): string[] => {
    return Array.from(itemsMap.keys()).filter((id) => !parentMap.has(id));
  };

  /**
   * Gets data at the current path level by navigating through the hierarchy
   * @param path Array of values representing the navigation path
   * @param fieldName Name of the field to match against (e.g., 'name')
   * @returns Array of items at the current level
   */
  const getDataAtPath = <K extends keyof T>(
    path: T[K][],
    fieldName: K
  ): T[] => {
    if (items.length === 0) return [];

    if (path.length === 0) {
      return items;
    }

    // Find the target node by walking through the path
    let currentNodeId: string | undefined;

    // Start from root level and navigate through path
    for (let i = 0; i < path.length; i++) {
      const pathSegment = path[i];

      if (i === 0) {
        // Find in root level
        currentNodeId = items.find(
          (node) => node[fieldName] === pathSegment
        )?.id;
      } else {
        // Find in children of current node
        const currentNode = itemsMap.get(currentNodeId!);
        if (currentNode?.children) {
          currentNodeId = currentNode.children.find(
            (child) => (child as T)[fieldName] === pathSegment
          )?.id;
        }
      }

      if (!currentNodeId) {
        return [];
      }
    }

    // Return children of the current node
    const currentNode = itemsMap.get(currentNodeId!);
    return (currentNode?.children as T[]) || [];
  };

  return {
    itemsMap,
    childrenMap,
    parentMap,
    getAllDescendants,
    getAncestors,
    areAllChildrenSelected,
    areAnyChildrenSelected,
    filterToHighestAncestors,
    getItemDepth,
    getLeafNodes,
    getRootNodes,
    getDataAtPath,
  };
}
